if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.productElement.classList.forEach((classApplied) => {
              if (classApplied.startsWith('color-') || classApplied === 'gradient')
                this.modalContent.classList.add(classApplied);
            });
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();

            // Bundle promotion
            this.applyBundleVariant(opener);

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      applyBundleVariant(opener) {
        // Only run on the Bundle Promotion section
        if (opener.dataset.bundleMode !== 'true') return;

        const bundleVariantId = opener.dataset.bundleVariantId;
        if (!bundleVariantId) return;

        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        const variantDataElement = variantPicker.querySelector(
          '[type="application/json"]'
        );

        if (!variantDataElement) return;

        let variants;

        try {
          variants = JSON.parse(variantDataElement.textContent);
        } catch (error) {
          console.error('Bundle: Unable to read variant data.', error);
          return;
        }

        const bundleVariant = variants.find(
          (variant) => String(variant.id) === String(bundleVariantId)
        );

        if (!bundleVariant) {
          console.warn(
            `Bundle: Variant ${bundleVariantId} was not found in this product.`
          );
          return;
        }

        const optionGroups = variantPicker.querySelectorAll(
          'select, fieldset'
        );

        if (!optionGroups.length) return;

        /*
         * Select all options belonging to the Bundle Variant.
         * We do this before triggering the change event so VariantSelects
         * receives the complete combination.
         */
        optionGroups.forEach((group, index) => {
          const targetValue = bundleVariant.options[index];

          if (!targetValue) return;

          if (group.tagName === 'SELECT') {
            const option = Array.from(group.options).find(
              (item) => item.value === targetValue
            );

            if (option) {
              group.value = targetValue;
            }
          }

          if (group.tagName === 'FIELDSET') {
            const input = Array.from(
              group.querySelectorAll('input[type="radio"]')
            ).find((item) => item.value === targetValue);

            if (input) {
              input.checked = true;
            }
          }
        });

        /*
         * Trigger VariantSelects once after all options have been selected.
         */
        const lastGroup = optionGroups[optionGroups.length - 1];

        if (lastGroup.tagName === 'SELECT') {
          lastGroup.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        } else {
          const checkedInput = lastGroup.querySelector(
            'input[type="radio"]:checked'
          );

          if (checkedInput) {
            checkedInput.dispatchEvent(
              new Event('change', {
                bubbles: true,
              })
            );
          }
        }

        /*
         * Add the Bundle Eligible label after VariantSelects
         * has finished updating the UI.
         */
        setTimeout(() => {
          this.addBundleEligibleLabels(variantPicker, bundleVariant);
        }, 50);
      }

      addBundleEligibleLabels(variantPicker, bundleVariant) {
        const optionGroups = variantPicker.querySelectorAll(
          'select, fieldset'
        );

        optionGroups.forEach((group, index) => {
          const targetValue = bundleVariant.options[index];

          if (!targetValue) return;

          /*
           * SELECT
           */
          if (group.tagName === 'SELECT') {
            const targetOption = Array.from(group.options).find(
              (option) => option.value === targetValue
            );

            if (!targetOption) return;

            const existingLabel = group.parentElement?.querySelector(
              '.bundle-eligible-note'
            );

            if (existingLabel) existingLabel.remove();

            const note = document.createElement('span');

            note.className = 'bundle-eligible-note';
            note.textContent = '✓ Bundle Eligible';

            group.insertAdjacentElement('afterend', note);
          }

          /*
           * FIELDSET / RADIO
           */
          if (group.tagName === 'FIELDSET') {
            const targetInput = Array.from(
              group.querySelectorAll('input[type="radio"]')
            ).find((input) => input.value === targetValue);

            if (!targetInput) return;

            const label = group.querySelector(
              `label[for="${CSS.escape(targetInput.id)}"]`
            );

            if (!label) return;

            if (
              label.querySelector('.bundle-eligible-note')
            ) {
              return;
            }

            const note = document.createElement('span');

            note.className = 'bundle-eligible-note';
            note.textContent = ' ✓ Bundle Eligible';

            label.appendChild(note);
          }
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}